import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Autocomplete,
} from "@mui/material";
import { DateTimePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import axios from "../../CustomAxios";
import { debounce } from "lodash";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 550,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

export default function Filter({ open, onClose, onApply, initialValues }) {
  const authData = JSON.parse(localStorage.getItem("AuthData"));

  const [search, setSearch] = useState(initialValues.search || "");
  const [status, setStatus] = useState(initialValues.status || "all");
  const [companyId, setCompanyId] = useState(initialValues.companyId || "");
  const [plateId, setPlateId] = useState(initialValues.plateId || "");
  const [fromDate, setFromDate] = useState(
    initialValues.fromDate ? new Date(initialValues.fromDate) : null
  );
  const [toDate, setToDate] = useState(
    initialValues.toDate ? new Date(initialValues.toDate) : null
  );

  const [companies, setCompanies] = useState([]);
  const [plates, setPlates] = useState([]);
  const [searchCompanyText, setSearchCompanyText] = useState("");
  const [searchPlateText, setSearchPlateText] = useState("");

  // Fetch companies
  const fetchCompanies = async (searchTerm = "") => {
    try {
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      const limit = searchTerm ? 20 : 5;
      const url = `/api/v1/${authData.type}/customer_company?limit=${limit}&search_keyword=${searchTerm}`;
      const { data } = await axios.get(url, config);
      if (data.success) {
        setCompanies(data.data.data || data.data || []);
      }
    } catch (e) {
      console.error("Error fetching companies", e);
    }
  };

  // Fetch plates – use the correct endpoint from Stock Auditing example
  const fetchPlates = async (searchTerm = "") => {
    try {
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      const limit = searchTerm ? 20 : 5;
      // This is the endpoint used in the Stock Auditing Filter
      const url = `/api/v1/${authData.type}/plate_inventory?page=1&limit=${limit}&search=${searchTerm}`;
      const { data } = await axios.get(url, config);
      if (data.success) {
        // The response could be data.data.data or data.data
        setPlates(data.data.data || data.data || []);
      }
    } catch (e) {
      console.error("Error fetching plates", e);
    }
  };

  const debouncedFetchCompanies = useCallback(debounce(fetchCompanies, 300), []);
  const debouncedFetchPlates = useCallback(debounce(fetchPlates, 300), []);

  useEffect(() => {
    fetchCompanies();
    fetchPlates();
  }, []);

  useEffect(() => {
    if (searchCompanyText) {
      debouncedFetchCompanies(searchCompanyText);
    } else {
      fetchCompanies();
    }
  }, [searchCompanyText, debouncedFetchCompanies]);

  useEffect(() => {
    if (searchPlateText) {
      debouncedFetchPlates(searchPlateText);
    } else {
      fetchPlates();
    }
  }, [searchPlateText, debouncedFetchPlates]);

  const handleApply = () => {
    onApply({
      search,
      status,
      companyId,
      plateId,
      fromDate: fromDate ? fromDate.toISOString() : "",
      toDate: toDate ? toDate.toISOString() : "",
    });
  };

  const handleReset = () => {
    setSearch("");
    setStatus("all");
    setCompanyId("");
    setPlateId("");
    setFromDate(null);
    setToDate(null);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Card sx={style}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Filters</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Search (Company / Plate)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={companies}
                  getOptionLabel={(option) => option.name || ""}
                  value={companies.find((c) => c._id === companyId) || null}
                  onChange={(e, newValue) => setCompanyId(newValue?._id || "")}
                  onInputChange={(e, newInputValue) => setSearchCompanyText(newInputValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Customer Company" size="small" />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={plates}
                  getOptionLabel={(option) =>
                    `${option.height}x${option.width}x${option.thickness} - ${option.brand?.name || ""
                    } (${option.technology?.name || ""})`
                  }
                  value={plates.find((p) => p._id === plateId) || null}
                  onChange={(e, newValue) => setPlateId(newValue?._id || "")}
                  onInputChange={(e, newInputValue) => setSearchPlateText(newInputValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Plate" size="small" />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={status}
                    label="Status"
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>

              <Grid item xs={6}>
                <Button onClick={handleReset} color="error" variant="outlined" fullWidth>
                  Reset All Filters
                </Button>
              </Grid>
              <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleApply} color="success" variant="contained" fullWidth>
                  Apply
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </CardContent>
      </Card>
    </Modal>
  );
}